import { useState, useEffect, useRef } from 'react';
import { Grid, Avatar, Menu, MenuItem, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes';
import { Button } from '@mui/material';
import {
  Check,
  CheckCircle,
  Close,
  Error,
  HelpOutlineOutlined,
  PriorityHigh,
  UploadOutlined,
  Warning,
} from '@mui/icons-material';
import CustomUrbanSimIcon from 'assets/customIcons/UrbanSimIcon';
import { useAppState } from 'components/contexts/State';
import './Notification.css';
import { useDispatch } from 'react-redux';
import { removeLayer, removeSpatialFilter } from '@carto/react-redux';
import DeleteConfirmationModal from 'components/views/main/common/DeleteConfirmationModal';
import TabularDataSteps from 'components/views/main/data/UploadDataStepModal/TabularDataSteps';
import { getDefaultCustomLegendMapping } from 'components/contexts/DefaultCustomLegendMapping';
import { setRightSheetOpen } from 'store/appSlice';
import FileUploadExplorer from 'components/views/main/data/UploadDataStepModal/FileUploadExplorer';
import DocumentDataModalParent from 'components/views/main/data/UploadDataStepModal/DocumentDataModelParent';
import { deleteUploadProcessById } from 'components/views/main/data/Services/DataRepoService';
import { getLayerFromType, getSqlTypes } from 'components/contexts/LayerSourceMapping';

export default function UserMenu() {
  const {
    userRoleDetails,
    setApiResponse,
    setCurrConvId,
    setChatData,
    setChatDataShowMore,
    uploadStatusDetails,
    isOpenUploadNotification,
    setOpenTabularStepOneDialog,
    setGoToDataLocationFromNotification,
    setOpenDocumentDataModal,
    setIsCustomLegendOpen,
    setCustomLegendConfig,
    fileTypeFromSocket,
    fileNameFromSocket,
    currProcessId,
    setCurrProcessId,
    setSignedUploadURL,
    setUploadStatusDetails,
    setOpenFileUploadExplorer,
    setDisabledUploadDataBtn,
    setFileTypeFromSocket,
    setFileNameFromSocket,
    setDataRepoTabValue,
    currentUploadXhr,
    calcelUploadModalOpen,
    setCancelUploadModalOpen,
    setIsOpenUploadNotification,
  } = useAppState();

  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [anchorElNotification, setAnchorElNotification] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState([]);

  const [deleteUploadModalOpen, setDeleteUploadModalOpen] = useState(false);

  const handleCancelUpload = () => {
    if (currentUploadXhr) {
      currentUploadXhr?.abort();
      const removeUploadProcess = async (processId) => {
        try {
          await deleteUploadProcessById(processId);
          setSignedUploadURL('');
          setUploadStatusDetails([]);
          setCurrProcessId('');
          setDisabledUploadDataBtn(false);
          setFileTypeFromSocket('');
          setFileNameFromSocket('');
          handleNotificationClose();
        } catch (err) {
          console.error(err.message);
        }
      };
      const processId = currProcessId;
      if (processId) removeUploadProcess(processId);
    }
    setCancelUploadModalOpen(false);
  };

  const handleDeleteUpload = () => {
    const removeUploadProcess = async (processId) => {
      setIsLoading(true);
      setLoadingType(['deleteUploadPost']);
      try {
        await deleteUploadProcessById(processId);
        setSignedUploadURL('');
        setUploadStatusDetails([]);
        setCurrProcessId('');
        setDisabledUploadDataBtn(false);
        setFileTypeFromSocket('');
        setFileNameFromSocket('');
        handleNotificationClose();
      } catch (err) {
        console.error(err.message);
      } finally {
        setIsLoading(false);
        setLoadingType([]);
        setDeleteUploadModalOpen(false);
      }
    };
    const processId = currProcessId;
    removeUploadProcess(processId);
  };

  useEffect(() => {
    if (!isOpenUploadNotification) handleNotificationClose();
    else {
      if (notificationButtonRef.current) {
        notificationButtonRef.current.click();
      }
    }
  }, [isOpenUploadNotification]);

  const notificationButtonRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userData = JSON.parse(localStorage.getItem('user')) || null;

  if (!userData) return null;

  const isUserMenuOpen = Boolean(userAnchorEl);
  const openNotification = Boolean(anchorElNotification);

  const handleUserMenuClick = (event) =>
    setUserAnchorEl(userAnchorEl ? null : event.currentTarget);

  const handleUserMenuClose = () => setUserAnchorEl(null);

  const handleLogout = () => {
    navigate(ROUTE_PATHS.LOGIN);
    localStorage.clear();
    handleUserMenuClose();
    setApiResponse([]);
    dispatch(removeSpatialFilter());
    getSqlTypes().forEach((type) => {
      dispatch(removeLayer(getLayerFromType(type)));
    });
    setCurrConvId(null);
    setChatData([]);
    setChatDataShowMore([]);
    setIsCustomLegendOpen(false);
    setCustomLegendConfig(getDefaultCustomLegendMapping);
    dispatch(setRightSheetOpen(false));
    if (currProcessId) {
      const removeUploadProcess = async (processId) => {
        try {
          await deleteUploadProcessById(processId);
          setSignedUploadURL('');
          setUploadStatusDetails([]);
          setCurrProcessId('');
          setDisabledUploadDataBtn(false);
          setFileTypeFromSocket('');
          setFileNameFromSocket('');
        } catch (err) {
          console.error(err.message);
        }
      };
      const processId = currProcessId;
      removeUploadProcess(processId);
    }
  };

  const handleNotificationClick = (event) => {
    setAnchorElNotification(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchorElNotification(null);
    setIsOpenUploadNotification(false);
  };

  const handleErrorAction = (isCancel) => {
    const removeUploadProcess = async (processId) => {
      try {
        await deleteUploadProcessById(processId);
      } catch (err) {
        console.error(err.message);
      }
    };
    const processId = currProcessId;
    removeUploadProcess(processId);
    setSignedUploadURL('');
    setUploadStatusDetails([]);
    setCurrProcessId('');
    setDisabledUploadDataBtn(false);
    setFileTypeFromSocket('');
    setFileNameFromSocket('');
    handleNotificationClose();
    if (!isCancel) {
      setOpenFileUploadExplorer(true);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        width: '100%',
        flexDirection: 'row-reverse',
      }}
    >
      
      <Grid container alignItems='center' item wrap='nowrap' sx={{ width: 'auto' }}>
        <div
          onClick={handleUserMenuClick}
          style={{
            color: '#616161',
            cursor: 'pointer',
          }}
          aria-controls={isUserMenuOpen ? 'user-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={isUserMenuOpen ? 'true' : undefined}
        >
          <Avatar
            sx={{
              '&.MuiAvatar-root': {
                width: '32px',
                height: '32px',
                background: 'rgba(0, 84, 164, 1)',
              },
            }}
            src={userData.photoURL ? userData.photoURL : ''}
          />
        </div>
        <Menu
          anchorEl={userAnchorEl}
          id='user-menu'
          open={isUserMenuOpen}
          onClose={handleUserMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              minWidth: '180px',
              mt: 1.5,
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              '&:focus-visible': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
            },
          }}
          sx={{
            '&:focus-visible': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={handleLogout}
            sx={{
              fontSize: '0.75rem',
              userSelect: 'none',
              '&:focus-visible': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Grid>
      {uploadStatusDetails.length > 0 &&
        Array.isArray(uploadStatusDetails) &&
        !uploadStatusDetails.every(
          (item) =>
            typeof item === 'object' && item.status && item.status === 'Not started'
        ) && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              width: '32px',
            }}
          >
            <div
              ref={notificationButtonRef}
              onClick={(event) => {
                handleNotificationClick(event);
              }}
              style={{
                height: '32px',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <UploadOutlined
                sx={{
                  height: '32px',
                  width: '32px',
                  color: '#0054A4',
                }}
              />
            </div>
            {uploadStatusDetails.some(
              (s) => s.status === 'In progress' && s.step !== 'Add details'
            ) && (
              <LinearProgress
                color='secondary'
                sx={{
                  width: '100%',
                  height: '2px',
                  '.MuiLinearProgress-bar': { backgroundColor: 'rgba(53, 149, 210, 1)' },
                }}
              />
            )}
            {uploadStatusDetails.find((step) => step.step === 'Upload')?.status ===
              'Completed' &&
              (uploadStatusDetails.find((step) => step.step === 'Add details')?.status ===
                'Not started' ||
                uploadStatusDetails.find((step) => step.step === 'Add details')
                  ?.status === 'In progress') && (
                <Warning
                  sx={{
                    width: '20px',
                    height: '20px',
                    fontSize: '20px',
                    color: 'rgba(239, 108, 0, 1)',
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                  }}
                />
              )}
            {uploadStatusDetails.some((step) => step.status === 'Error') && (
              <Error
                sx={{
                  width: '20px',
                  height: '20px',
                  fontSize: '20px',
                  color: 'rgba(211, 47, 47, 1)',
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                }}
              />
            )}
            {uploadStatusDetails.find((step) => step.step === 'Finalize')?.status ===
              'Completed' && (
              <CheckCircle
                sx={{
                  width: '20px',
                  height: '20px',
                  fontSize: '20px',
                  color: 'rgba(46, 125, 50, 1)',
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                }}
              />
            )}
            <Menu
              id='basic-menu-notification'
              anchorEl={anchorElNotification}
              open={openNotification}
              onClose={handleNotificationClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              sx={{
                '.MuiPaper-root:focus-visible, .MuiList-root:focus-visible': {
                  outline: 'none !important',
                  borderRadius: '4px !important',
                  boxShadow:
                    '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20) !important',
                },
                '.MuiPaper-root': {
                  borderRadius: '4px !important',
                  boxShadow:
                    '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
                },
                '.MuiButtonBase-root:focus-visible': {
                  outline: 'none !important',
                  borderRadius: '0 !important',
                  boxShadow: 'none !important',
                },
                '.MuiMenu-list': {
                  maxHeight: 'none !important',
                },
                marginTop: '40px',
              }}
            >
              <div style={{ padding: '8px 16px', width: '320px' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <div className='text-heading-style-1'>Upload status</div>
                  <Close
                    sx={{
                      height: '24px',
                      width: '24px',
                      fontSize: '24px',
                      color: '#002547',
                      cursor: 'pointer',
                    }}
                    onClick={handleNotificationClose}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '24px',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  {uploadStatusDetails.map((obj, ind) => (
                    <div className='notification-container'>
                      <div className='notification-title-container'>
                        <div
                          className={
                            'notification-circle' +
                            (obj.status === 'In progress' || obj.status === 'Error'
                              ? ' notification-circle-active'
                              : '') +
                            (obj.status === 'Completed'
                              ? ' notification-circle-success'
                              : '') +
                            (obj.step === 'Add details' &&
                            uploadStatusDetails[ind - 1].status === 'Completed'
                              ? ' notification-circle-active'
                              : '')
                          }
                        >
                          <div
                            className='notification-circle-text'
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '24px',
                              width: '24px',
                            }}
                          >
                            {!(obj.status === 'Completed') && <span>{ind + 1}</span>}
                            {obj.status === 'Completed' && (
                              <Check sx={{ color: 'white' }} />
                            )}
                          </div>
                        </div>
                        <div
                          className={
                            'notification-title-text' +
                            (obj.status === 'In progress'
                              ? ' notification-title-text-active'
                              : '') +
                            (obj.status === 'Completed'
                              ? ' notification-title-text-success'
                              : '') +
                            (obj.status === 'Error'
                              ? ' notification-title-text-error'
                              : '') +
                            (obj.step === 'Add details' &&
                            uploadStatusDetails[ind - 1].status === 'Completed'
                              ? ' notification-title-text-active'
                              : '')
                          }
                        >
                          {(obj.status === 'Not started' ||
                            (obj.step === 'Add details' &&
                              obj.status === 'In progress')) && (
                            <>
                              {obj.step === 'Add details' &&
                                (obj.status === 'In progress' ||
                                  obj.status === 'Not started') &&
                                uploadStatusDetails[ind - 1].status === 'Completed' && (
                                  <>
                                    <div className='icon-text-flex-container'>
                                      <Warning
                                        sx={{
                                          width: '16px',
                                          height: '16px',
                                          fontSize: '16px',
                                          color: 'rgba(239, 108, 0, 1)',
                                        }}
                                      />
                                      <div style={{ fontWeight: '500' }}>
                                        Details needed
                                      </div>
                                    </div>
                                  </>
                                )}
                              {!(
                                obj.step === 'Add details' &&
                                (obj.status === 'In progress' ||
                                  obj.status === 'Not started') &&
                                uploadStatusDetails[ind - 1].status === 'Completed'
                              ) && <>{obj.step}</>}
                            </>
                          )}
                          {obj.status === 'Error' && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <div
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  background: '#d32f2f',
                                  borderRadius: '50%',
                                }}
                              >
                                <PriorityHigh
                                  sx={{
                                    height: '12px',
                                    width: '12px',
                                    fontSize: '12px',
                                    color: '#fff',
                                  }}
                                />
                              </div>
                              <span style={{ fontWeight: '500' }}>Error</span>
                            </div>
                          )}
                          {obj.status === 'Completed' && (
                            <div style={{ fontWeight: '400' }}>
                              {obj.step === 'Upload' && 'Upload complete'}
                              {obj.step === 'Add details' && 'Details added'}
                              {obj.step === 'Finalize' && 'Finalized'}
                            </div>
                          )}
                          {obj.status === 'In progress' && (
                            <div style={{ fontWeight: '500' }}>
                              {obj.step === 'Upload' && 'Uploading file'}
                              {obj.step === 'Finalize' && 'Finalizing'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        {obj.step === 'Upload' && (
                          <>
                            {obj.status === 'In progress' && (
                              <div className='notification-file-upload-detail-container'>
                                <div className='notification-file-upload-status-container'>
                                  <div
                                    className='normal-text-style-1'
                                    style={{ color: 'rgba(0, 37, 71, 0.6)' }}
                                  >
                                    {fileNameFromSocket}
                                  </div>
                                  <div className='notification-percentage-wrapper'>
                                    <div className='notification-percentage-container'>
                                      <div
                                        className='notification-percentage'
                                        style={{ width: obj.percentage_complete + '%' }}
                                      ></div>
                                    </div>
                                    <div className='notification-title-text'>
                                      {obj.percentage_complete}%
                                    </div>
                                  </div>
                                </div>
                                {obj.percentage_complete !== undefined &&
                                  obj.percentage_complete !== null &&
                                  parseInt(obj.percentage_complete, 10) < 50 && (
                                    <div className='notification-cancel-file-upload-container'>
                                      <Button
                                        variant='outlined'
                                        color='error'
                                        className='error-outlined-button-small'
                                        onClick={() => {
                                          setCancelUploadModalOpen(true);
                                        }}
                                      >
                                        Cancel upload
                                      </Button>
                                    </div>
                                  )}
                                {obj.percentage_complete !== undefined &&
                                  obj.percentage_complete !== null &&
                                  parseInt(obj.percentage_complete, 10) >= 50 && (
                                    <div
                                      style={{ margin: '4px 0' }}
                                      className='normal-text-style-3'
                                    >
                                      This process can’t be canceled and may take up to 20
                                      minutes for some files.
                                    </div>
                                  )}
                              </div>
                            )}
                            {obj.status === 'Error' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ minHeight: '16px' }}
                              >
                                <div
                                  className='notification-title-text'
                                  style={{
                                    paddingBottom: '8px',
                                    color: '#d32f2f',
                                  }}
                                >
                                  {obj.message.includes('400') &&
                                    fileTypeFromSocket === 'table' && (
                                      <>
                                        There are no supported file types in your zip, or
                                        it’s corrupt.
                                        <ul>
                                          <li>
                                            Zip files should contain files such as .shp,
                                            .geojson, .gpkg, .gdb, etc.
                                          </li>
                                          <li>
                                            If your file is corrupt please try to validate
                                            the file in another software or contact the
                                            original data provider.
                                          </li>
                                        </ul>
                                      </>
                                    )}
                                  {obj.message.includes('409') &&
                                    fileTypeFromSocket === 'table' &&
                                    fileNameFromSocket &&
                                    fileNameFromSocket.includes('.') &&
                                    fileNameFromSocket.split('.').pop() === 'zip' && (
                                      <>
                                        Table already exists.
                                        <ul>
                                          <li>
                                            Either delete it from the{' '}
                                            <span
                                              style={{
                                                cursor: 'pointer',
                                                fontWeight: '500',
                                              }}
                                              onClick={() => {
                                                handleNotificationClose();
                                                navigate(ROUTE_PATHS.DATA);
                                                setDataRepoTabValue(0);
                                              }}
                                            >
                                              Data Library
                                            </span>
                                          </li>
                                          <li>
                                            Or rename all the files in your zip and
                                            re-upload.
                                          </li>
                                        </ul>
                                      </>
                                    )}
                                  {obj.message.includes('409') &&
                                    fileTypeFromSocket === 'table' &&
                                    fileNameFromSocket &&
                                    fileNameFromSocket.includes('.') &&
                                    fileNameFromSocket.split('.').pop() === 'geojson' && (
                                      <>
                                        Table already exists.
                                        <ul>
                                          <li>
                                            Either delete it from the{' '}
                                            <span
                                              style={{
                                                cursor: 'pointer',
                                                fontWeight: '500',
                                              }}
                                              onClick={() => {
                                                handleNotificationClose();
                                                navigate(ROUTE_PATHS.DATA);
                                                setDataRepoTabValue(0);
                                              }}
                                            >
                                              Data Library
                                            </span>
                                          </li>
                                          <li>Or rename your file and re-upload.</li>
                                        </ul>
                                      </>
                                    )}
                                  {obj.message.includes('409') &&
                                    fileTypeFromSocket === 'table' &&
                                    fileNameFromSocket &&
                                    fileNameFromSocket.includes('.') &&
                                    (fileNameFromSocket.split('.').pop() === 'gpkg' ||
                                      fileNameFromSocket.split('.').pop() === 'gdb') && (
                                      <>
                                        Table already exists.
                                        <ul>
                                          <li>
                                            Either delete it from the{' '}
                                            <span
                                              style={{
                                                cursor: 'pointer',
                                                fontWeight: '500',
                                              }}
                                              onClick={() => {
                                                handleNotificationClose();
                                                navigate(ROUTE_PATHS.DATA);
                                                setDataRepoTabValue(0);
                                              }}
                                            >
                                              Data Library
                                            </span>
                                          </li>
                                          <li>
                                            Or rename the top layer in your file and
                                            re-upload.
                                          </li>
                                        </ul>
                                      </>
                                    )}
                                  {obj.message.includes('500') && (
                                    <>
                                      <div>
                                        Our server had an issue while processing your
                                        file.
                                      </div>
                                      <div>
                                        Please try your upload again if the error
                                        persists.
                                      </div>
                                    </>
                                  )}
                                  <div
                                    style={{ paddingTop: '8px' }}
                                    className='notification-cancel-file-upload-container'
                                  >
                                    <Button
                                      variant='outlined'
                                      color='error'
                                      className='error-outlined-button-small'
                                      onClick={() => handleErrorAction(true)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant='contained'
                                      color='secondary'
                                      className='secondary-contained-button-small'
                                      sx={{ margin: '0 !important' }}
                                      onClick={() => handleErrorAction(false)}
                                    >
                                      Re-upload
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {obj.status === 'Completed' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ height: '16px' }}
                              ></div>
                            )}
                            <DeleteConfirmationModal
                              title={'Upload in progress'}
                              open={calcelUploadModalOpen}
                              onClose={() => {
                                if (
                                  !(isLoading && loadingType.includes('cancelUploadPost'))
                                )
                                  setCancelUploadModalOpen(false);
                              }}
                              confirmationMessage={
                                'Your upload will be cancelled if you leave SMART METRO now.'
                              }
                              deleteButtonText='Cancel upload'
                              onDelete={handleCancelUpload}
                              isLoading={
                                isLoading && loadingType.includes('cancelUploadPost')
                              }
                            />
                          </>
                        )}
                        {obj.step === 'Add details' && (
                          <>
                            {(obj.status === 'Not started' ||
                              obj.status === 'In progress') &&
                              uploadStatusDetails[ind - 1].status !== 'Completed' && (
                                <div
                                  className='notification-file-upload-detail-container'
                                  style={{ height: '16px' }}
                                ></div>
                              )}
                            {(obj.status === 'Not started' ||
                              obj.status === 'In progress') &&
                              uploadStatusDetails[ind - 1].status === 'Completed' && (
                                <div className='notification-file-upload-detail-container'>
                                  <div className='notification-cancel-file-upload-container'>
                                    <Button
                                      variant='outlined'
                                      color='error'
                                      className='error-outlined-button-small'
                                      onClick={() => {
                                        setDeleteUploadModalOpen(true);
                                      }}
                                    >
                                      Delete upload
                                    </Button>
                                    <Button
                                      variant='contained'
                                      color='secondary'
                                      className='secondary-contained-button-small'
                                      onClick={() => {
                                        if (fileTypeFromSocket === 'document') {
                                          setOpenDocumentDataModal({
                                            value: true,
                                            isFromNotification: true,
                                          });
                                        } else {
                                          setOpenTabularStepOneDialog({
                                            value: true,
                                            isFromNotification: true,
                                          });
                                        }
                                      }}
                                      sx={{ margin: '0 !important' }}
                                    >
                                      Add details
                                    </Button>
                                  </div>
                                  <DeleteConfirmationModal
                                    open={deleteUploadModalOpen}
                                    onClose={() => {
                                      if (
                                        !(
                                          isLoading &&
                                          loadingType.includes('deleteUploadPost')
                                        )
                                      )
                                        setDeleteUploadModalOpen(false);
                                    }}
                                    confirmationMessage={
                                      'Are you sure you want to delete your upload?'
                                    }
                                    deleteButtonText='Delete upload'
                                    onDelete={handleDeleteUpload}
                                    isLoading={
                                      isLoading &&
                                      loadingType.includes('deleteUploadPost')
                                    }
                                  />
                                </div>
                              )}
                            {obj.status === 'Error' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ minHeight: '16px' }}
                              >
                                <div
                                  className='notification-title-text'
                                  style={{
                                    paddingBottom: '8px',
                                    color: '#d32f2f',
                                    fontSize: '12px',
                                  }}
                                >
                                  {obj.message}
                                </div>
                              </div>
                            )}
                            {obj.status === 'Completed' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ height: '16px' }}
                              ></div>
                            )}
                          </>
                        )}
                        {obj.step === 'Finalize' && (
                          <>
                            {obj.status === 'In progress' && (
                              <div
                                style={{ borderLeft: 'none', minHeight: '16px' }}
                                className='notification-file-upload-detail-container'
                              >
                                <LinearProgress
                                  color='secondary'
                                  sx={{
                                    width: '100%',
                                    height: '4px',
                                    '.MuiLinearProgress-bar': {
                                      backgroundColor: 'rgba(53, 149, 210, 1)',
                                    },
                                  }}
                                />
                              </div>
                            )}
                            {obj.status === 'Error' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ borderLeft: 'none', minHeight: '16px' }}
                              >
                                <div
                                  className='notification-title-text'
                                  style={{
                                    paddingBottom: '8px',
                                    color: '#d32f2f',
                                    fontSize: '12px',
                                  }}
                                >
                                  {obj.message}
                                </div>
                              </div>
                            )}
                            {obj.status === 'Completed' && (
                              <div
                                className='notification-file-upload-detail-container'
                                style={{ borderLeft: 'none', minHeight: '16px' }}
                              >
                                <div
                                  style={{ justifyContent: 'flex-start' }}
                                  className='notification-cancel-file-upload-container'
                                >
                                  <Button
                                    variant='contained'
                                    color='success'
                                    className='success-contained-button-small'
                                    onClick={() => {
                                      setGoToDataLocationFromNotification(true);
                                      setSignedUploadURL('');
                                      setUploadStatusDetails([]);
                                      setCurrProcessId('');
                                      setDisabledUploadDataBtn(false);
                                      setFileTypeFromSocket('');
                                      setFileNameFromSocket('');
                                      handleNotificationClose();
                                      navigate(ROUTE_PATHS.DATA);
                                      if (fileTypeFromSocket === 'document')
                                        setDataRepoTabValue(1);
                                      else setDataRepoTabValue(0);
                                    }}
                                  >
                                    Go to dataset
                                  </Button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Menu>
          </div>
        )}
      {/* <HelpOutlineOutlined
        sx={{
          height: '32px',
          width: '32px',
          color: 'rgba(0,0,0,0.56)',
          cursor: 'pointer',
        }}
      /> */}
      {userRoleDetails &&
        userRoleDetails.resources &&
        userRoleDetails.resources.includes('manage_urbansim') && (
          <Button
            variant='contained'
            startIcon={
              <CustomUrbanSimIcon
                sx={{
                  fontSize: '18px',
                  height: '18px !important',
                  width: '18px !important',
                  color: 'white',
                }}
              />
            }
            sx={{
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '500',
              lineHeight: '22px',
              letterSpacing: '0',
              boxShadow:
                '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important',
              width: '210px',
            }}
            onClick={() => {
              window.open('https://prod.urbansim.com/', '_blank');
            }}
          >
            Access Land Use Simulation
          </Button>
        )}
      <TabularDataSteps />
      <DocumentDataModalParent />
      <FileUploadExplorer />
    </div>
  );
}
